import {Action} from '@reduxjs/toolkit'
import {OfficeModel} from '../models/OfficeModel'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  OfficesRequested: '[Request Offices] Action',
  OfficesLoaded: '[Load Offices] Offices API',
  OfficesFailed: '[Load Offices] Offices API Failed',
  OfficeRequested: '[Request Office] Action',
  OfficeLoaded: '[Load Office] Office API',
  OfficeFailed: '[Load Office] Office API Failed',
  OfficeCreated: '[Create Office] Action',
  OfficeUpdated: '[Update Office] Action',
  OfficeDeleted: '[Delete Office] Action',
}

const initialOfficeState: IOfficeState = {
  offices: [],
  office: undefined,
  loading: false,
  error: undefined,
}

export interface IOfficeState {
  offices: OfficeModel[]
  office?: OfficeModel
  loading: boolean
  error?: string
}

export const reducer = (state: IOfficeState = initialOfficeState, action: ActionWithPayload<IOfficeState>) => {
  switch (action.type) {
    case actionTypes.OfficesRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.OfficesLoaded: {
      const offices = action.payload?.offices || []
      return {...state, offices, loading: false, error: undefined}
    }

    case actionTypes.OfficesFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.OfficeRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.OfficeLoaded: {
      const office = action.payload?.office
      return {...state, office, loading: false, error: undefined}
    }

    case actionTypes.OfficeFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.OfficeCreated: {
      const office = action.payload?.office
      if (office) {
        return {...state, offices: [...state.offices, office], loading: false}
      }
      return state
    }

    case actionTypes.OfficeUpdated: {
      const office = action.payload?.office
      if (office) {
        const updatedOffices = state.offices.map(o => (o.id === office.id ? office : o))
        return {...state, offices: updatedOffices, office, loading: false}
      }
      return state
    }

    case actionTypes.OfficeDeleted: {
      const officeId = action.payload?.office?.id
      if (officeId) {
        const filteredOffices = state.offices.filter(o => o.id !== officeId)
        return {...state, offices: filteredOffices, loading: false}
      }
      return state
    }

    default:
      return state
  }
}

export const actions = {
  requestOffices: () => ({
    type: actionTypes.OfficesRequested,
  }),
  fulfillOffices: (offices: OfficeModel[]) => ({
    type: actionTypes.OfficesLoaded,
    payload: {offices},
  }),
  failOffices: (error: string) => ({
    type: actionTypes.OfficesFailed,
    payload: {error},
  }),
  requestOffice: () => ({
    type: actionTypes.OfficeRequested,
  }),
  fulfillOffice: (office: OfficeModel) => ({
    type: actionTypes.OfficeLoaded,
    payload: {office},
  }),
  failOffice: (error: string) => ({
    type: actionTypes.OfficeFailed,
    payload: {error},
  }),
  createOffice: (office: OfficeModel) => ({
    type: actionTypes.OfficeCreated,
    payload: {office},
  }),
  updateOffice: (office: OfficeModel) => ({
    type: actionTypes.OfficeUpdated,
    payload: {office},
  }),
  deleteOffice: (office: OfficeModel) => ({
    type: actionTypes.OfficeDeleted,
    payload: {office},
  }),
}
